import { Text, Alert, Heading } from "@idg-web-platform/pulse";
import { useEffect, useState } from "preact/hooks";
import { analyticsContentViewed } from "../utils/analyticsContentViewed.ts";
import { NavigationWrapper } from "../components/navigation/navigationWrapper.tsx";
import { OrderSummary, renderSummary } from "./orderSummary.tsx";
import { renderInStoreSummary } from "./inStoreOrderSummary.tsx";
import { Tabs, Tab } from "./components/tabs/tabs.tsx";
import { OrderChannel } from "./entities/OrderChannel.ts";
import css from "./ordersPage.module.css";
import margins from "@idg-web-platform/pulse/cssModules/margins.module.css";

OrdersPage.tagName = "control-orders-page";

type OrdersPageProps = {
  /** Online orders (pre-filtered by backend) */
  onlineOrders: OrderSummary[];
  /** In-store/stationary orders (pre-filtered by backend) */
  stationaryOrders: OrderSummary[];
  failures: number;
};

const ORDER_TABS: Tab[] = [
  { id: OrderChannel.ONLINE, label: "Online" },
  { id: OrderChannel.STATIONARY, label: "Im Store" },
];

export function OrdersPage({ onlineOrders, stationaryOrders, failures }: OrdersPageProps) {
  const [activeTab, setActiveTab] = useState<string>(
    onlineOrders.length === 0 ? OrderChannel.STATIONARY : OrderChannel.ONLINE,
  );

  const currentOrders = activeTab === OrderChannel.ONLINE ? onlineOrders : stationaryOrders;

  const totalOrders = onlineOrders.length + stationaryOrders.length + failures;

  useEffect(() => {
    analyticsContentViewed("orders", totalOrders === 0 ? "no_orders" : undefined);
  }, []);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Track tab switch analytics
    analyticsContentViewed("orders", `tab_${tabId.toLowerCase()}`);
  };

  return (
    <>
      <NavigationWrapper currentPath="/c/orders">
        <Heading text="Meine Bestellungen" sizeStep={2} />

        <Tabs tabs={ORDER_TABS} activeTabId={activeTab} onTabChange={handleTabChange} />

        {failures > 0 && (
          <Alert
            type="error"
            headline="Entschuldigung, wir haben ein technisches Problem"
            description={`Es geht nichts verloren und wir arbeiten daran, ${failures === 1 ? "deine Bestellung" : `deine ${failures} Bestellungen`} und die Aktualisierungen zum Bestellstatus schnellstmöglich wieder anzeigen zu können.`}
            showCloseButton={false}
            class={margins.pulseMarginBottomM}
          />
        )}

        {/* Empty state for Online tab - only show if no failures */}
        {activeTab === OrderChannel.ONLINE && currentOrders.length === 0 && failures === 0 && (
          <>
            <Text class={css.noOrdersHeader}>
              Du hast noch keine Bestellung auf intersport.de vorgenommen.
            </Text>
            <Text>
              Sobald du eine Bestellung durchgeführt hast, kannst du alle Details an dieser Stelle
              jederzeit abrufen.
            </Text>
          </>
        )}

        {/* Empty state for Im Store tab - only show if no failures */}
        {activeTab === OrderChannel.STATIONARY && currentOrders.length === 0 && failures === 0 && (
          <>
            <Text class={css.noOrdersHeader}>
              Du hast noch keine Einkäufe in einem INTERSPORT Store vorgenommen.
            </Text>
            <Text>
              Sobald du einen Einkauf durchgeführt und deine Kundenkarte vorgezeigt hast, kannst du
              alle Details an dieser Stelle jederzeit abrufen.
            </Text>
          </>
        )}

        {/* Online orders list */}
        {activeTab === OrderChannel.ONLINE &&
          currentOrders.length > 0 &&
          currentOrders.map((order) => renderSummary(order))}

        {/* In-store orders list */}
        {activeTab === OrderChannel.STATIONARY &&
          currentOrders.length > 0 &&
          currentOrders.map((order) => renderInStoreSummary(order))}
      </NavigationWrapper>
    </>
  );
}
